import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyTriggerPatterns } from './companyTriggerApi';
import SuppressedPanel from './SuppressedPanel';
import BarList from './TriggerBars';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_GRID
} from '../../../../component/common/designSystem';
import {
  AREA_RAMP,
  INTENSITY_RAMP,
  DURATION_RAMP,
  RANK_RAMP,
  areaColor
} from './triggerTheme';

/**
 * Trigger Trace → Patterns.
 *
 * What sets people off, which part of life it comes from, what it feels like,
 * how hard it lands and how long it lingers.
 *
 * THE CROSS-TABS ARE THE POINT OF THIS PAGE. A ranked list of triggers tells
 * you what gets logged most; it says nothing about what actually costs people
 * something. Work might be the most-logged area simply because people are at
 * work when they open the app. Pairing each area with its average intensity and
 * how much of it gets worked through is what separates "logged often" from
 * "hurts most" from "nobody gets past it" — three very different problems with
 * three different responses.
 *
 * EVERY CELL IS GATED. A cross-tab narrows a population fast: "Work, severe,
 * unresolved" can be two people. Rows below the backend's cell minimum still
 * appear — an area nobody logs is worth knowing about — but every derived
 * figure on them arrives as null and renders as a dash, never as a small
 * number that could be one person.
 *
 * `fetcher` is a prop so an unscoped hub could render this page unchanged.
 */
const TriggerPatterns = ({ fetcher = fetchCompanyTriggerPatterns }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (loader) return <Loader />;
  if (!stats || stats.suppressed) return <SuppressedPanel minimum={stats?.minimum ?? 0} />;

  /** Null means withheld or not applicable — never rendered as zero. */
  const orDash = (value, suffix = '') => (value == null ? '-' : `${value}${suffix}`);

  const areaIntensityRows = stats.areaBreakdown.filter((row) => row.avgIntensity != null);

  const areaIntensityOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [({ dataPointIndex }) => areaColor(areaIntensityRows[dataPointIndex]?.label)],
    plotOptions: {
      bar: { horizontal: true, borderRadius: 3, barHeight: '55%', distributed: true }
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: CHART_GRID,
    xaxis: {
      categories: areaIntensityRows.map((row) => row.label),
      // The slider only ever goes to 10, so the axis does too — otherwise a
      // group averaging 6 would fill the bar and read as "as bad as it gets".
      max: 10,
      tickAmount: 5
    },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) =>
          `${val} out of 10 — from ${formatBigNumber(areaIntensityRows[dataPointIndex]?.logs || 0)} logs`
      }
    }
  };

  const cellNote = (
    <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
      A dash means fewer than {stats.cellMinimum} logs sit behind that figure, so it is withheld
      rather than shown — at that size an average is one person&apos;s week.
    </p>
  );

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          icon={STAT_ICONS.logs}
          title="Triggers Logged"
          value={formatBigNumber(stats.logs)}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Work-Related"
          value={stats.logs > 0 ? `${stats.workShare}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.rating}
          title="Average Intensity"
          value={stats.avgIntensity == null ? '-' : `${stats.avgIntensity} / 10`}
        />
        <StatCard
          icon={STAT_ICONS.custom}
          title="Own Trigger Names"
          value={formatBigNumber(stats.customVocabulary.names)}
        />
      </div>

      {/* ---------- The mix ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Which Part of Life"
          subtitle="The area each trigger was tagged with"
          data={stats.lifeAreaSplit}
          colors={AREA_RAMP}
          unit="logs"
          totalLabel="Logs"
          legendLimit={8}
        />
        <MiniDonutCard
          title="How Hard It Hit"
          subtitle="Self-rated intensity, 1 to 10"
          data={stats.intensitySplit}
          colors={INTENSITY_RAMP}
          unit="logs"
          totalLabel="Rated"
          legendLimit={5}
        />
        <MiniDonutCard
          title="How Long It Lasted"
          subtitle="How long the shift stayed with them"
          data={stats.durationSplit}
          colors={DURATION_RAMP}
          unit="logs"
          totalLabel="Logs"
          legendLimit={6}
        />
      </div>

      {/* ---------- Ranked lists ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <Panel
          title="What Sets People Off"
          subtitle="The app's eight built-in triggers, plus everything else pooled"
        >
          {stats.triggerSplit.length > 0 ? (
            <BarList
              rows={stats.triggerSplit}
              unit="logs"
              colorFor={(index) => RANK_RAMP[index % RANK_RAMP.length]}
            />
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
          {/* The trigger field is free text. Anything outside the app's own
              eight options is mapped to one pooled label inside the database
              query, so a name somebody typed can never reach this screen. */}
          <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
            &ldquo;Something else&rdquo; is every trigger people named themselves, counted together.
            Those names are never shown — people write about real situations, and sometimes real
            colleagues.
          </p>
        </Panel>
        <Panel title="What It Felt Like" subtitle="Emotions attached to those triggers">
          {stats.emotionSplit.length > 0 ? (
            <BarList
              rows={stats.emotionSplit}
              unit="logs"
              colorFor={(index) => RANK_RAMP[index % RANK_RAMP.length]}
            />
          ) : (
            <PanelEmpty>No emotions recorded yet.</PanelEmpty>
          )}
          <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
            People can pick more than one feeling per trigger, so these add up to more than 100%.
            {stats.coverage.emotion < 100
              ? ` ${stats.coverage.emotion}% of logs have any emotion recorded.`
              : ''}
          </p>
        </Panel>
      </div>

      {/* ---------- Which parts of life cost the most ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-6">
        <Panel
          title="How Hard Each Area Hits"
          subtitle="Average self-rated intensity by part of life"
        >
          {areaIntensityRows.length > 0 ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[
                { name: 'Average intensity', data: areaIntensityRows.map((row) => row.avgIntensity) }
              ]}
              options={areaIntensityOptions}
            />
          ) : (
            <PanelEmpty>
              No part of life has enough rated logs behind it to report an average.
            </PanelEmpty>
          )}
        </Panel>
        <Panel
          title="Area by Area"
          subtitle="How often, how hard, and how much of it people get past"
        >
          {stats.areaBreakdown.length > 0 ? (
            <>
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Part of life</th>
                    <th className="py-2 px-4 font-medium text-center">Logs</th>
                    <th className="py-2 px-4 font-medium text-center">Intensity</th>
                    <th className="py-2 px-4 font-medium text-center">Worked through</th>
                    <th className="py-2 font-medium text-right">Still open</th>
                  </>
                }
              >
                {stats.areaBreakdown.map((row) => (
                  <tr key={row.label} className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: areaColor(row.label) }}
                        />
                        {row.label}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-center">{formatBigNumber(row.logs)}</td>
                    <td className="py-2 px-4 text-center">{orDash(row.avgIntensity, ' / 10')}</td>
                    <td className="py-2 px-4 text-center">{orDash(row.resolutionRate, '%')}</td>
                    <td className="py-2 text-right">{orDash(row.openRate, '%')}</td>
                  </tr>
                ))}
              </PanelTable>
              {cellNote}
            </>
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Trigger by trigger ---------- */}
      <div className="mt-6">
        <Panel
          title="Trigger by Trigger"
          subtitle="Which ones come up most, which land hardest, and which people get past"
        >
          {stats.triggerBreakdown.length > 0 ? (
            <>
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Trigger</th>
                    <th className="py-2 px-4 font-medium text-center">Logs</th>
                    <th className="py-2 px-4 font-medium text-center">Intensity</th>
                    <th className="py-2 px-4 font-medium text-center">Worked through</th>
                    <th className="py-2 font-medium text-right">Still open</th>
                  </>
                }
              >
                {stats.triggerBreakdown.map((row, index) => (
                  <tr key={row.label} className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: RANK_RAMP[index % RANK_RAMP.length] }}
                        />
                        {row.label}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-center">{formatBigNumber(row.logs)}</td>
                    <td className="py-2 px-4 text-center">{orDash(row.avgIntensity, ' / 10')}</td>
                    <td className="py-2 px-4 text-center">{orDash(row.resolutionRate, '%')}</td>
                    <td className="py-2 text-right">{orDash(row.openRate, '%')}</td>
                  </tr>
                ))}
              </PanelTable>
              {cellNote}
            </>
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Emotion by emotion ---------- */}
      <div className="mt-6">
        <Panel
          title="Emotion by Emotion"
          subtitle="Which feelings show up with the hardest triggers, and which ones people move past"
        >
          {stats.emotionBreakdown.length > 0 ? (
            <>
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Emotion</th>
                    <th className="py-2 px-4 font-medium text-center">Logs</th>
                    <th className="py-2 px-4 font-medium text-center">Intensity</th>
                    <th className="py-2 px-4 font-medium text-center">Worked through</th>
                    <th className="py-2 font-medium text-right">Still open</th>
                  </>
                }
              >
                {stats.emotionBreakdown.map((row, index) => (
                  <tr key={row.label} className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: RANK_RAMP[index % RANK_RAMP.length] }}
                        />
                        {row.label}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-center">{formatBigNumber(row.logs)}</td>
                    <td className="py-2 px-4 text-center">{orDash(row.avgIntensity, ' / 10')}</td>
                    <td className="py-2 px-4 text-center">{orDash(row.resolutionRate, '%')}</td>
                    <td className="py-2 text-right">{orDash(row.openRate, '%')}</td>
                  </tr>
                ))}
              </PanelTable>
              {cellNote}
            </>
          ) : (
            <PanelEmpty>No emotions recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
        Intensity, part of life and emotion are all optional when logging — {stats.coverage.intensity}
        % of logs carry an intensity and {stats.coverage.lifeArea}% carry a part of life, so these
        charts describe the logs that were filled in rather than all of them.
      </p>
      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
        Nobody is named on this page, and no figure here can be traced to a person. What people
        wrote about each moment is never read by this dashboard.
      </p>
    </div>
  );
};

TriggerPatterns.propTypes = {
  fetcher: PropTypes.func
};

export default TriggerPatterns;
